import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowDown } from 'lucide-react';
interface MetricStairsCompactProps {
  type: 'length' | 'volume' | 'weight';
  from?: string;
  to?: string;
  value?: number;
  highlight?: string;
  showAnimation?: boolean;
  mode?: 'stimulus' | 'support';
}
const METRIC_UNITS = {
  length: ['km', 'hm', 'dam', 'm', 'dm', 'cm', 'mm'],
  volume: ['kl', 'hl', 'dal', 'l', 'dl', 'cl', 'ml', 'm3'],
  weight: ['kg', 'hg', 'dag', 'g', 'dg', 'cg', 'mg']
};
const UNIT_LABELS = {
  // Length
  km: 'kilometer',
  hm: 'hectometer',
  dam: 'decameter',
  m: 'meter',
  dm: 'decimeter',
  cm: 'centimeter',
  mm: 'millimeter',
  // Volume
  kl: 'kiloliter',
  hl: 'hectoliter',
  dal: 'decaliter',
  l: 'liter',
  dl: 'deciliter',
  cl: 'centiliter',
  ml: 'milliliter',
  m3: 'kubieke meter',
  // Weight
  kg: 'kilogram',
  hg: 'hectogram',
  dag: 'decagram',
  g: 'gram',
  dg: 'decigram',
  cg: 'centigram',
  mg: 'milligram'
};
export function MetricStairsCompact({
  type,
  from,
  to,
  value,
  highlight,
  showAnimation = true,
  mode = 'support'
}: MetricStairsCompactProps) {
  const units = METRIC_UNITS[type];
  // ── STIMULUS MODE: compact unit ladder only, no calculation/tip ───────────
  if (mode === 'stimulus') {
    // If from/to conversion: show compact arrow chain
    if (from && to) {
      const fromIndex = units.indexOf(from);
      const toIndex = units.indexOf(to);
      if (fromIndex === -1 || toIndex === -1) return null;
      const isGoingDown = toIndex > fromIndex;
      const startIdx = Math.min(fromIndex, toIndex);
      const endIdx = Math.max(fromIndex, toIndex);
      const relevantUnits = units.slice(startIdx, endIdx + 1).filter(u => u !== 'm3');
      return <div className="flex flex-col items-center gap-2 py-2" data-id="element-9590">
          <div className="flex items-center gap-1.5 flex-wrap justify-center" data-id="element-9591">
            {relevantUnits.map((unit, idx) => <Fragment key={unit} data-id="element-9592">
                <div className={`px-3 py-1.5 rounded-lg font-black text-base border-2 shadow-sm
                  ${unit === from || unit === to ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-slate-600 border-slate-200'}`} data-id="element-9593">
                  {unit}
                </div>
                {idx < relevantUnits.length - 1 && <div className="flex flex-col items-center" data-id="element-9594">
                    <span className="text-slate-400 text-xs font-bold" data-id="element-9595">
                      {isGoingDown ? '×10' : '÷10'}
                    </span>
                    <span className="text-slate-400 text-sm" data-id="element-9596">→</span>
                  </div>}
              </Fragment>)}
          </div>
        </div>;
    }
    // Overview mode: compact vertical list
    return <div className="flex flex-col gap-1 py-2 items-center" data-id="element-9597">
        {units.filter(u => u !== 'm3').map(unit => <div key={unit} className={`px-4 py-1 rounded-lg font-bold text-sm border
            ${unit === highlight ? 'bg-indigo-600 text-white border-indigo-700 shadow-sm' : 'bg-white text-slate-600 border-slate-200'}`} data-id="element-9598">
              {unit}
            </div>)}
      </div>;
  }
  // ── SUPPORT MODE: more compact conversion panel (optimized for explanatory use) ──
  if (from && to && value !== undefined) {
    const fromIndex = units.indexOf(from);
    const toIndex = units.indexOf(to);
    if (fromIndex === -1 || toIndex === -1) {
      return <div className="text-red-500" data-id="element-9599">Ongeldige eenheden</div>;
    }
    let steps = Math.abs(toIndex - fromIndex);
    let isGoingDown = toIndex > fromIndex;
    let result: number;
    // Special case: m³ ↔ liter
    if (from === 'm3' && to === 'l' || from === 'l' && to === 'm3') {
      if (from === 'm3') {
        result = value * 1000;
        isGoingDown = true;
      } else {
        result = value / 1000;
        isGoingDown = false;
      }
      return <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4 border-2 border-indigo-200" data-id="element-9600">
          <div className="flex items-center justify-center gap-3 mb-3" data-id="element-9601">
            <div className="flex flex-col items-center bg-indigo-600 text-white rounded-lg px-4 py-2 border-2 border-indigo-700 shadow-md" data-id="element-9602">
              <span className="text-xl font-bold" data-id="element-9603">{from}</span>
            </div>
            <div className="flex flex-col items-center" data-id="element-9604">
              <ArrowRight className="w-6 h-6 text-indigo-600" data-id="element-9605" />
              <span className="text-xs font-bold text-indigo-700" data-id="element-9606">
                {isGoingDown ? '×1000' : '÷1000'}
              </span>
            </div>
            <div className="flex flex-col items-center bg-indigo-600 text-white rounded-lg px-4 py-2 border-2 border-indigo-700 shadow-md" data-id="element-9607">
              <span className="text-xl font-bold" data-id="element-9608">{to}</span>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-indigo-200 text-center" data-id="element-9609">
            <p className="text-sm text-slate-700" data-id="element-9610">
              <span className="font-bold text-indigo-900" data-id="element-9611">
                {value} {from}
              </span>
              {' = '}
              <span className="font-bold text-emerald-700" data-id="element-9612">
                {result.toLocaleString()} {to}
              </span>
            </p>
          </div>
        </div>;
    }
    result = isGoingDown ? value * Math.pow(10, steps) : value / Math.pow(10, steps);
    const startIdx = Math.min(fromIndex, toIndex);
    const endIdx = Math.max(fromIndex, toIndex);
    const relevantUnits = units.slice(startIdx, endIdx + 1).filter(u => u !== 'm3');
    return <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4 border-2 border-indigo-200" data-id="element-9613">
        <div className="flex items-center justify-center gap-2 mb-3 flex-wrap" data-id="element-9614">
          {relevantUnits.map((unit, idx) => <Fragment key={unit} data-id="element-9615">
              <div className={`flex flex-col items-center ${unit === from || unit === to ? 'bg-indigo-600 text-white' : 'bg-white text-slate-700'} rounded-lg px-3 py-2 border-2 ${unit === from || unit === to ? 'border-indigo-700 shadow-md' : 'border-slate-200'}`} data-id="element-9616">
                <span className="text-lg font-bold" data-id="element-9617">{unit}</span>
              </div>
              {idx < relevantUnits.length - 1 && <div className="flex flex-col items-center" data-id="element-9618">
                  <ArrowRight className="w-5 h-5 text-indigo-600" data-id="element-9619" />
                  <span className="text-xs font-bold text-indigo-700" data-id="element-9620">
                    {isGoingDown ? '×10' : '÷10'}
                  </span>
                </div>}
            </Fragment>)}
        </div>
        <div className="bg-white rounded-lg p-3 border border-indigo-200 text-center" data-id="element-9621">
          <p className="text-sm text-slate-700" data-id="element-9622">
            <span className="font-bold text-indigo-900" data-id="element-9623">
              {value} {from}
            </span>
            {' = '}
            <span className="font-bold text-emerald-700" data-id="element-9624">
              {result} {to}
            </span>
          </p>
        </div>
      </div>;
  }
  // Default overview (compact)
  return <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4 border-2 border-indigo-200" data-id="element-9625">
      <h4 className="font-bold text-indigo-900 mb-3 text-center text-sm" data-id="element-9626">
        {type === 'length' && '📏 Lengtematen'}
        {type === 'volume' && '🧃 Inhoudsmaten'}
        {type === 'weight' && '⚖️ Gewichten'}
      </h4>
      <div className="flex flex-col gap-1" data-id="element-9627">
        {units.filter(u => u !== 'm3').map((unit, idx) => <div key={unit} className={`flex items-center justify-between p-2 rounded-lg text-sm ${unit === highlight ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-slate-700'} border ${unit === highlight ? 'border-indigo-700' : 'border-slate-200'}`} data-id="element-9628">
              <span className="font-bold" data-id="element-9629">{unit}</span>
              {idx < units.filter(u => u !== 'm3').length - 1 && <span className="text-xs opacity-75" data-id="element-9630">×10 ↓</span>}
            </div>)}
      </div>
    </div>;
}